import {
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import {
  useDashboard,
  useApiQuery,
  useInfiniteApiQuery,
  useTeam,
} from '@vrplatform/data';
import { hasValue, type Maybe } from '@vrplatform/utils';
import { useState } from 'react';
import type { BaseCustomFilterProps } from './_types';
import {
  getContactsFilterQuery,
  mapContactToSelectItem,
} from './ContactsFilter.utils';

export const STORYBOOK_CONTACTS_FILTER_QUERY_KEY = 'embed-contacts-filter';
export const STORYBOOK_CONTACTS_FILTER_VALUE_QUERY_KEY =
  'embed-contacts-filter-value';

interface Props extends BaseCustomFilterProps {
  contactIds: Maybe<string[]>;
  setContactIds: (value: string[] | null) => void;
  type: 'owner' | 'vendor' | 'all';
  clearable?: boolean;
}

export const ContactsFilter = ({
  contactIds: initial,
  setContactIds,
  label: initialLabel,
  type = 'all',
  clearable = true,
}: Props) => {
  const [dashboard] = useDashboard();
  const [{ id: teamId }] = useTeam();

  const label =
    initialLabel || type === 'owner'
      ? 'Owner'
      : type === 'vendor'
        ? 'Vendor'
        : 'Contact';

  const contactIds = initial || [];
  const [search, setSearch] = useState('');
  const gate = useSelectQueryGate({ value: contactIds });
  const shouldPrefetchOptions = dashboard === 'owner' && !search.trim();
  const shouldFetchOptions = gate.shouldFetchOptions || shouldPrefetchOptions;

  const queryData = useInfiniteApiQuery(
    async (api, signal, variables, pageParam) => {
      const response = await api.GET('/contacts', {
        params: {
          query: getContactsFilterQuery({
            page: pageParam,
            search: variables.search,
            type: variables.type,
          }),
        },
        signal,
      });

      if (response.error) {
        throw response.error;
      }

      return {
        list: response.data.data.map(mapContactToSelectItem),
        aggregate: response.data.pagination.total,
        nextPage: response.data.pagination.nextPage,
      };
    },
    {
      skip: !teamId || !shouldFetchOptions,
      storybookQueryKey: STORYBOOK_CONTACTS_FILTER_QUERY_KEY,
      queryGroup: 'owners',
      uniqueQueryKey: 'contacts-filter',
      queryKeys: ['contacts'],
      variables: {
        teamId,
        search: search.trim(),
        type,
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? null,
    }
  );

  const { data } = useApiQuery(
    async (api, signal, variables) => {
      if (!variables.contactIds.length) {
        return { value: [] as SelectItem[] };
      }

      const contacts = await Promise.all(
        variables.contactIds.map(async (id) => {
          const response = await api.GET('/contacts/{id}', {
            params: {
              path: {
                id,
              },
            },
            signal,
          });

          if (response.error) {
            throw response.error;
          }

          return response.data;
        })
      );

      return {
        value: contacts.map(mapContactToSelectItem),
      };
    },
    {
      skip: !teamId || !gate.shouldFetchValue,
      queryGroup: 'owners',
      uniqueQueryKey: 'contacts-filter-value',
      queryKeys: ['contacts'],
      keepPreviousData: true,
      storybookQueryKey: STORYBOOK_CONTACTS_FILTER_VALUE_QUERY_KEY,
      variables: {
        teamId,
        contactIds: (contactIds || []).filter(hasValue),
      },
    }
  );

  const value = gate.hasValue ? data?.value || [] : [];

  if (dashboard === 'owner' && !search.trim() && shouldPrefetchOptions) {
    if (queryData.isLoading) return <LoadingIndicator size="18" />;
    if ((queryData.data?.pages[0]?.list.length ?? 0) < 2) return null;
  }

  return (
    <Filter.Select
      value={value}
      setValue={(next) =>
        setContactIds(next.length ? next.map((entry) => entry.value) : null)
      }
      label={label}
      type="multiple"
      withinPortal
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
      hideClearButton={!clearable}
    />
  );
};
